import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './TowerOfLondon.module.css';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getUserId } from '../../utils/userId';
import { v4 as uuidv4 } from 'uuid';
import { doc, setDoc, Timestamp } from '@firebase/firestore';
import { db } from '../../utils/firebase';
import { getCognitiveTestSetId } from '../../utils/testId';
import { 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  Button 
} from '@mui/material';

// --- React DnD Imports ---
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';

// Game constants
const MAX_HEIGHT = { A: 3, B: 2, C: 1 };
const PEGS = ['A', 'B', 'C'] as const;
const COLORS = { R: 'red', G: 'green', B: 'blue' };

type PegId = typeof PEGS[number];
type PegState = Record<PegId, string[]>;

const problems = [
  { id: 1, minMoves: 2, initial: { A: ['G', 'R'], B: ['B'], C: [] }, goal: { A: [], B: ['B','G'], C: ['R'] } },
  { id: 2, minMoves: 2, initial: { A: ['G', 'R'], B: ['B'], C: [] }, goal: { A: ['G'], B: ['R'], C: ['B'] } },
  { id: 3, minMoves: 3, initial: { A: ['G', 'R'], B: ['B'], C: [] }, goal: { A: ['G', 'B'], B: ['R'], C: [] } },
  { id: 4, minMoves: 3, initial: { A: ['G', 'R'], B: ['B'], C: [] }, goal: { A: ['G'], B: ['R','B'], C: [] } },
  { id: 5, minMoves: 4, initial: { A: ['G', 'R'], B: ['B'], C: [] }, goal: { A: ['R','G'], B: ['B'], C: [] } }
];

// --- 1. DraggableRing 컴포넌트 (분리 유지) ---
const DraggableRing = ({ ball, pegId, isTop, isSolved, isSelectedRing, onDragStart }: any) => {
    const [{ isDragging }, drag, preview] = useDrag({
      type: 'RING',
      item: () => {
        onDragStart(pegId);
        return { fromPeg: pegId };
      },
      canDrag: isTop && !isSolved,
      collect: (monitor) => ({ 
        isDragging: !!monitor.isDragging() 
      }),
    });

    // preview를 통해 드래그 시 이미지가 보이도록 설정
    return (
      <div
        ref={(node) => { if (isTop) drag(node); }}
        className={`${styles.ring} ${isSelectedRing ? styles.selectedRing : ''}`}
        style={{ 
          backgroundColor: COLORS[ball as keyof typeof COLORS], 
          opacity: isDragging ? 0.5 : 1, // 0으로 하면 아예 안 보일 수 있으니 0.5 권장
          cursor: isTop ? 'grab' : 'default',
          touchAction: 'none', // 브라우저 스크롤 방해 금지 핵심
          zIndex: isDragging ? 100 : 1
        }}
      />
    );
  };

// --- 2. Peg 컴포넌트 (renderPeg 대신 컴포넌트로 변경하여 훅 사용 가능하게 함) ---
const Peg = ({ 
  peg, isGoal, pegState, draggedFrom, availableTargets, 
  onSelectPeg, onDropRing, isSolved 
}: any) => {
  const maxHeight = MAX_HEIGHT[peg as keyof typeof MAX_HEIGHT];
  const pegHeight = maxHeight * 25;
  const isSelected = !isGoal && draggedFrom === peg;
  const isAvailableTarget = !isGoal && availableTargets.includes(peg);
  const nextRingIndex = pegState[peg as PegId].length;
  const placeholderBottom = 10 + (20 + 1) * nextRingIndex;

  const movingRingColor = (isAvailableTarget && draggedFrom)
    ? COLORS[pegState[draggedFrom as PegId].slice(-1)[0] as keyof typeof COLORS] : undefined;

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'RING',
    canDrop: (item: any) => !isGoal && !isSolved && item.fromPeg !== peg && pegState[peg as PegId].length < maxHeight,
    drop: (item: any) => onDropRing(item.fromPeg, peg),
    collect: (monitor) => ({ isOver: !!monitor.isOver(), canDrop: !!monitor.canDrop() }),
  });

  const guideOpacity = isOver && canDrop ? 0.8 : 0.3;

  return (
    <div
      ref={(node) => { if (!isGoal) drop(node); }}
      className={styles.peg}
      onClick={() => !isGoal && onSelectPeg(peg)}
      style={{ 
        cursor: !isGoal ? 'pointer' : 'default',
        backgroundColor: isOver && canDrop ? 'rgba(0, 255, 0, 0.2)' : 'transparent'
      }}
    >
      <div className={styles.pegLabel}>{peg}</div>
      <div className={styles.pegBase} style={{ '--peg-height': `${pegHeight}px` } as any} />
      <div className={styles.stack} style={{ '--peg-height': `${pegHeight}px` } as any}>
        {isAvailableTarget && movingRingColor && (
          <div 
            className={`${styles.ring} ${styles.placeholderRing}`} 
            style={{ 
              bottom: `${placeholderBottom}px`, 
              backgroundColor: movingRingColor,
              opacity: guideOpacity, // 진해지는 효과 적용
              boxShadow: isOver && canDrop ? '0 0 15px white' : 'none', // 오버 시 빛나는 효과 추가
              transition: 'opacity 0.2s, box-shadow 0.2s' // 부드러운 전환
            }} 
          />
        )}
        {[...pegState[peg as PegId]].reverse().map((ball, i) => (
          <DraggableRing 
            key={i} 
            ball={ball} 
            pegId={peg} 
            isTop={i === 0} 
            isSolved={isSolved}
            isSelectedRing={isSelected && i === 0}
            onDragStart={onSelectPeg} 
          />
        ))}
      </div>
    </div>
  );
};

const TowerOfLondon: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  const [gameState, setGameState] = useState<'start' | 'playing' | 'results'>('start');
  const [problemIndex, setProblemIndex] = useState(0);
  const [state, setState] = useState<PegState>({ A: [], B: [], C: [] });
  const [draggedFrom, setDraggedFrom] = useState<string | null>(null);
  const [availableTargets, setAvailableTargets] = useState<string[]>([]);
  const [moves, setMoves] = useState(0);
  const [isGoalReached, setIsGoalReached] = useState(false);
  const [isSolved, setIsSolved] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [timer, setTimer] = useState(0);
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);

  const problem = problems[problemIndex];

  useLayoutEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    const timer = setTimeout(() => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (gameState === 'playing') {
      setState(JSON.parse(JSON.stringify(problem.initial)));
      setMoves(0);
      setDraggedFrom(null);
      setIsGoalReached(false);
      setIsSolved(false);
      setStartTime(Date.now());
    }
  }, [problemIndex, gameState, problem.initial]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (gameState === 'playing') {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [gameState]);

  const executeMove = useCallback((from: string, to: string) => {
    if (from === to || state[to as PegId].length >= MAX_HEIGHT[to as PegId]) {
      setDraggedFrom(null);
      return;
    }
    const fromStack = [...state[from as PegId]];
    const ball = fromStack.pop();
    const toStack = [...state[to as PegId], ball!];
    const newState = { ...state, [from]: fromStack, [to]: toStack };
    setState(newState);
    setMoves(prev => prev + 1);
    setDraggedFrom(null);
    const isCorrect = PEGS.every(
      (p) => JSON.stringify(newState[p]) === JSON.stringify(problem.goal[p])
    );
    if (isCorrect) setIsGoalReached(true);
  }, [state, problem.goal]);

  const handlePegClickOrDragStart = (pegId: string) => {
    if (isSolved) return;
    if (draggedFrom === null) {
      if (state[pegId as PegId].length > 0) setDraggedFrom(pegId);
    } else {
      if (draggedFrom === pegId) setDraggedFrom(null);
      else executeMove(draggedFrom, pegId);
    }
  };

  useEffect(() => {
    if (draggedFrom) {
      setAvailableTargets(PEGS.filter(p => p !== draggedFrom && state[p as PegId].length < MAX_HEIGHT[p as PegId]));
    } else {
      setAvailableTargets([]);
    }
  }, [draggedFrom, state]);

  const handleFinish = () => { saveResultsToFirebase(); navigate(`${prefix}/cognitive/check/new`); };

  const handleCommit = () => {
    if (isSolved) return; 
    setIsSolved(true);
    const elapsed = startTime ? (Date.now() - startTime) / 1000 : 0;
    let gained = 0;
    if (moves === problem.minMoves) {
        if (elapsed <= 15) gained = 3;
        else if (elapsed <= 30) gained = 2;
        else if (elapsed <= 60) gained = 1;
    }
    setScore(prev => prev + gained);
    setTimeout(() => { if (problemIndex < problems.length - 1) setProblemIndex(problemIndex + 1); else setGameState('results'); }, 500); 
  };

  const saveResultsToFirebase = async () => {
    try {
      const userId = getUserId();
      const testSetId = getCognitiveTestSetId();
      if (!userId || !testSetId) return;
      const docId = `${userId}_${Date.now()}_${uuidv4()}`;
      await setDoc(doc(db, "userCognitiveResponse", docId), {
        userId, testSetId, testType: "test4", timestamp: Timestamp.now(), results: { totalScore: score },
      });
    } catch (e) { console.error(e); }
  };

  const renderPreviewPeg = (peg: string) => {
    const pegState = { A: ['G', 'R'], B: ['B'], C: [] };
    return (
      <div key={peg} className={styles.previewPeg}>
        <div className={styles.previewPegLabel}>{peg}</div>
        <div className={styles.previewPegBase} />
        <div className={styles.previewStack}>
          {[...pegState[peg as PegId]].reverse().map((ball, i) => (
            <div key={i} className={styles.previewRing} style={{ backgroundColor: COLORS[ball as keyof typeof COLORS] }} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true, delayTouchStart: 0, ignoreEvents: false }}>
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(`${prefix}/cognitive/check/new`)}><ArrowBack /></button>
          <span className={styles.headerText}>{t('check_before_3')}</span>
        </div>
        
        {gameState === 'start' && (
          <>
            <div className={styles.startScreen}>
                <p className={styles.digitSpanInstructions1}>{t('tower_of_london_instructions_1')}</p>
                <div className={styles.previewContainer}>
                  <div className={styles.previewBasePlate} />
                  {PEGS.map(p => renderPreviewPeg(p))}
                </div>
                <p className={styles.digitSpanInstructions2}>{t('tower_of_london_instructions_2')}</p>
            </div>
            <button onClick={() => setGameState('playing')} className={styles.startButton}>{t("start_test_button")}</button>
          </>
        )}

        {gameState === 'playing' && (
          <div className={styles.testScreen}>
            <div className={styles.gameInfo}>
              <div className={styles.questionNumber}>{t('problem')} {problem.id}</div>
              <div className={styles.moves}>{t('moves')}: {moves} / {problem.minMoves}</div>
            </div>
            <div className={styles.gameContainer}>
              <div className={styles.gameSection}>
                <h3 className={styles.sectionTitle}>{t('goal')}</h3>
                <div className={styles.pegContainer}>
                  <div className={styles.basePlate} />
                  {PEGS.map(p => (
                    <Peg 
                      key={p} peg={p} isGoal={true} pegState={problem.goal} isSolved={isSolved}
                      draggedFrom={null} availableTargets={[]} 
                    />
                  ))}
                </div>
              </div>
              <div className={styles.gameSection}>
                <h3 className={styles.sectionTitle}>{t('move_to_match')}</h3>
                <div className={styles.pegContainer}>
                  <div className={styles.basePlate} />
                  {PEGS.map(p => (
                    <Peg 
                      key={p} peg={p} isGoal={false} pegState={state} isSolved={isSolved}
                      draggedFrom={draggedFrom} availableTargets={availableTargets}
                      onSelectPeg={handlePegClickOrDragStart} onDropRing={executeMove}
                    />
                  ))}
                </div>
              </div>
            </div>
            {isGoalReached ? (
              <button onClick={handleCommit} className={`${styles.skipButton} ${styles.commitButton}`} disabled={isSolved}>{t('commit')}</button>
            ) : (
              <button onClick={() => setIsSkipModalOpen(true)} className={styles.skipButton}>{t('skip')}</button>
            )}
          </div>
        )}

        {gameState === 'results' && (
          <>
            <div className={styles.resultsScreen}>
                <h2>{t('test_finished')}</h2>
                <div className={styles.resultsSummary}><p>{t('final_score')}: {score}</p><p>{t('time')}: {timer}s</p></div>
            </div>
            <button className={styles.finishButton} onClick={handleFinish}>{t('finish_button_1')}</button>
          </>
        )}

        <Dialog open={isSkipModalOpen} onClose={() => setIsSkipModalOpen(false)} PaperProps={{ style: { borderRadius: '24px', padding: '16px', maxWidth: '320px' } }}>
          <DialogContent><DialogContentText style={{ color: '#333', textAlign: 'center', fontWeight: '500', lineHeight: '1.6', wordBreak: 'keep-all' }}>{t('skip_warning_message', '현재 문제를 건너뛰고 다음 문제로 이동하시겠습니까?\n현재 문제의 점수는 0점으로 처리됩니다.')}</DialogContentText></DialogContent>
          <DialogActions style={{ justifyContent: 'space-around', paddingBottom: '16px' }}>
            <Button onClick={() => setIsSkipModalOpen(false)} style={{ color: '#888', fontSize: '1rem' }}>{t('cancel')}</Button>
            <Button onClick={() => { setIsSkipModalOpen(false); setIsSolved(true); setTimeout(() => { if (problemIndex < problems.length - 1) setProblemIndex(problemIndex + 1); else setGameState('results'); }, 100); }} style={{ color: '#037DFF', fontSize: '1rem', fontWeight: 'bold' }}>{t('ok')}</Button>
          </DialogActions>
        </Dialog>
      </div>
    </DndProvider>
  );
};

export default TowerOfLondon;